import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  IAccountService,
  IPropertySelection,
  IUserContext,
  TOPICS,
} from "@origin-digital/event-dispatcher";
import { isAuthenticated } from "@origin-digital/platform-helpers";
import { useRespondWhenDefined } from "../hooks/useRespondWhenDefined";
import { useAutoRefreshingData } from "../hooks/useAutoRefreshingData";
import { usePropertySelection } from "../hooks/usePropertySelection";
import { fetchUserContextV3 } from "./UserContextService";

export type UserContextType = {
  user?: IUserContext;
  loading: boolean;
  properties?: IPropertySelection[];
  property: IPropertySelection | null;
  setProperty: (property: IPropertySelection | null, persist?: boolean) => void;
  setService: (accountService: IAccountService) => void;
};

export const UserContext = createContext<UserContextType>({
  user: undefined,
  loading: true,
  properties: undefined,
  property: null,
  setProperty: () => {},
  setService: () => {},
});

export const useUserContext = () => useContext(UserContext);

const isLoginTokenPresent = () => {
  if (typeof window !== "undefined") {
    // Happy to use the lazy synchronous check here, doesn't need to be 100% accurate
    // check window and document to avoid SSR failures
    return isAuthenticated();
  }
  return false;
};

export const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const [loggedIn, setLoggedIn] = useState(isLoginTokenPresent());

  const tokenPresent = isLoginTokenPresent();
  if (tokenPresent !== loggedIn) {
    setLoggedIn(isLoginTokenPresent());
  }

  const {
    data: user,
    loading,
    refresh: refreshUserContext,
  } = useAutoRefreshingData<IUserContext | undefined>(fetchUserContextV3);
  useRespondWhenDefined(TOPICS.FETCH_USER_CONTEXT, user);

  useEffect(() => {
    refreshUserContext();
  }, [refreshUserContext, loggedIn]);

  const { properties, property, setProperty, setService } =
    usePropertySelection(user);
  useRespondWhenDefined(TOPICS.FETCH_ALL_PROPERTIES, properties);

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        properties,
        property,
        setProperty,
        setService,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
